// Same-origin launcher the billing popup is opened at directly, instead of
// opening `about:blank` and navigating the handle after the portal-session
// fetch resolves. Navigating a freshly-opened blank popup after an async gap is
// unreliable (the first navigation is dropped in some browsers, leaving a dead
// blank window), so the popup instead loads this route synchronously and does
// the fetch + redirect in its own context. See PortalRedirectRoute and
// useOpenBillingPortal.
export const PORTAL_REDIRECT_PATH = "/billing/portal-redirect"

// Which portal flow to open (cancel / plan change / payment update / invoices).
export const PORTAL_LAUNCH_FLOW_PARAM = "flow"
// Surface that opened the portal — forwarded for analytics only.
export const PORTAL_LAUNCH_SOURCE_PARAM = "source"
// Organization slug, so the popup resolves the same billing org as the opener.
const PORTAL_LAUNCH_SLUG_PARAM = "slug"
// Workspace slug carried from the originating workspace context.
export const PORTAL_LAUNCH_WORKSPACE_PARAM = "workspace"
// One-time nonce proving the launch came from an in-app billing control rather
// than a third party opening this route for a signed-in user.
export const PORTAL_LAUNCH_NONCE_PARAM = "n"

const NONCE_STORAGE_KEY = "billing:portal-launch-nonce"
const NONCE_TTL_MS = 60_000

// Seed a one-time nonce (readable by the same-origin popup via localStorage) so
// the launcher can confirm the launch originated from our own app. Returns the
// nonce, or null if we couldn't generate one.
export function createPortalLaunchNonce(): string | null {
  let nonce: string
  try {
    nonce = crypto.randomUUID()
  } catch {
    return null
  }
  try {
    window.localStorage.setItem(
      NONCE_STORAGE_KEY,
      JSON.stringify({ nonce, expires: Date.now() + NONCE_TTL_MS }),
    )
  } catch {
    // localStorage unavailable (e.g. private mode); consume fails open below.
  }
  return nonce
}

// Validate and consume the launch nonce. Fails closed on a missing/expired/
// mismatched nonce, but open when localStorage is unavailable so a legitimate
// launch is never hard-blocked on browsers that disable storage.
export function consumePortalLaunchNonce(value: string | null): boolean {
  let raw: string | null
  try {
    raw = window.localStorage.getItem(NONCE_STORAGE_KEY)
    window.localStorage.removeItem(NONCE_STORAGE_KEY)
  } catch {
    return false
  }
  if (value === null || raw === null) {
    return false
  }
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) {
      return false
    }
    const { nonce, expires } = parsed as { nonce?: unknown; expires?: unknown }
    return (
      nonce === value && typeof expires === "number" && expires > Date.now()
    )
  } catch {
    return false
  }
}

// Stripe-hosted billing portal URLs are always https on stripe.com (e.g.
// billing.stripe.com). The launcher forwards to whatever the backend returns,
// so validate it before replacing to guard against an unexpected destination.
export function isStripePortalUrl(value: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return false
  }
  if (parsed.protocol !== "https:") {
    return false
  }
  const host = parsed.hostname.toLowerCase()
  return host === "stripe.com" || host.endsWith(".stripe.com")
}

// Build the absolute launcher URL. Absolute (not relative) because the caller
// assigns it to `window.open`, and the popup fallback assigns it to the current
// tab's location.
export function buildPortalLauncherHref(
  origin: string,
  {
    flow,
    source,
    slug,
    workspaceSlug,
    nonce,
  }: {
    flow: string
    source?: string
    slug?: string
    workspaceSlug?: string
    nonce?: string
  },
): string {
  const params = new URLSearchParams({ [PORTAL_LAUNCH_FLOW_PARAM]: flow })
  if (source !== undefined) {
    params.set(PORTAL_LAUNCH_SOURCE_PARAM, source)
  }
  if (slug !== undefined) {
    params.set(PORTAL_LAUNCH_SLUG_PARAM, slug)
  }
  if (workspaceSlug !== undefined) {
    params.set(PORTAL_LAUNCH_WORKSPACE_PARAM, workspaceSlug)
  }
  if (nonce !== undefined) {
    params.set(PORTAL_LAUNCH_NONCE_PARAM, nonce)
  }
  return `${origin}${PORTAL_REDIRECT_PATH}?${params.toString()}`
}
